<script>
  import { ui } from '../lib/stores/ui.svelte.js';
  import { workspace, callBbAi } from '../lib/stores/workspace.js';
  import { auth } from '../lib/stores/auth.svelte.js';
  import { supabase } from '../lib/supabase.js';
  import { showToast } from '../lib/stores/toast.js';

  let messages = $state([]);
  let inputText = $state('');
  let sending = $state(false);
  let scrollEl;

  let mode = $state(null); // null = picker, 'ask' | 'practical' | 'internal' | 'personal'
  // The company Elvie (Miles 2026-11-05): the header names the company; memory is per person per company, shared with HQ.
  let companyName = $state('');
  $effect(() => {
    const pid = workspace.partner_id;
    if (!pid) { companyName = ''; return; }
    supabase.rpc('fn_bb_my_companies').then(({ data }) => { companyName = (data || []).find((c) => c.partner_id === pid)?.name || ''; });
  });
  let segmentPicker = $state(false);
  let pickedSegment = $state(null); // { kind, id, name }

  const segmentOptions = $derived.by(() => {
    const opts = [];
    for (const a of (workspace.audiences || []).filter(x => !x.deleted_at)) opts.push({ kind: 'audience', id: a.id, name: a.name });
    for (const d of (workspace.deals || workspace.offers || []).filter(x => !x.deleted_at)) opts.push({ kind: 'deal', id: d.id, name: d.name });
    for (const s of (workspace.steps || []).filter(x => !x.deleted_at)) opts.push({ kind: 'step', id: s.id, name: s.step_name });
    for (const c of (workspace.chains || workspace.journeys || [])) opts.push({ kind: 'chain', id: c.id, name: c.name });
    return opts;
  });

  function pickMode(m) {
    mode = m;
    if (m === 'segment') segmentPicker = true;
    else segmentPicker = false;
  }

  function pickSegment(seg) {
    pickedSegment = seg;
    segmentPicker = false;
  }

  function resetMode() {
    mode = null;
    segmentPicker = false;
    pickedSegment = null;
  }

  async function loadThread() {
    if (!workspace.id || !auth.profile?.id) return;
    const q = supabase.from('bb_coach_threads').select('messages').eq('owner_profile_id', auth.profile.id);
    const { data } = workspace.partner_id
      ? await q.eq('partner_id', workspace.partner_id).maybeSingle()
      : await q.eq('workspace_id', workspace.id).maybeSingle();
    messages = data?.messages || [];
    setTimeout(scrollToBottom, 50);
  }

  async function sendMessage() {
    const trimmed = inputText.trim();
    if (!trimmed || sending) return;
    if (!mode) return;
    if (mode === 'segment' && !pickedSegment) return;

    const userMsg = { role: 'user', content: trimmed, ts: new Date().toISOString() };
    messages = [...messages, userMsg];
    inputText = '';
    sending = true;
    setTimeout(scrollToBottom, 50);
    try {
      const payload = {
        workspace_id: workspace.id,
        user_message: trimmed,
        mode
      };
      if (mode === 'segment' && pickedSegment) {
        payload.segment_kind = pickedSegment.kind;
        payload.segment_id = pickedSegment.id;
      }
      const r = await callBbAi('coach_chat', payload);
      if (r?.error) throw new Error(r.detail?.message || r.error);
      messages = [...messages, {
        role: 'assistant',
        content: r.reply,
        ts: new Date().toISOString(),
        recommendation: r.recommendation || null,
        used_web_search: r.used_web_search || false
      }];
      setTimeout(scrollToBottom, 50);
    } catch (e) {
      showToast('Elvie failed: ' + e.message, 'error');
      messages = messages.slice(0, -1);
      inputText = trimmed;
    } finally {
      sending = false;
    }
  }

  function scrollToBottom() {
    if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  $effect(() => {
    if (ui.elvieOpen) loadThread();
  });
  $effect(() => {
    if (workspace.id && ui.elvieOpen) loadThread();
  });
</script>

{#if ui.elvieOpen}
  <aside class="elvie-panel" onclick={(e) => e.stopPropagation()}>
    <header class="elvie-header">
      <div class="elvie-header-left">
        <img class="elvie-avatar-orb" src="https://res.cloudinary.com/dj0spw2w8/image/upload/v1778104240/BLV_icon_transparent_dcpgpl.png" alt="" />
        <div>
          <h3>Elvie{#if companyName} <span class="elvie-for">· for {companyName}</span>{/if}</h3>
          <p class="elvie-subtitle">
            {#if mode === 'ask'}Ask about the company
            {:else if mode === 'practical'}Analyze · External
            {:else if mode === 'internal'}Analyze · Internal
            {:else if mode === 'personal'}Life fit
            {:else}Pick what you need help with
            {/if}
          </p>
        </div>
      </div>
      <div class="elvie-header-right">
        {#if mode}
          <button class="elvie-mode-switch" onclick={resetMode} type="button" title="Change mode">↺</button>
        {/if}
        <button class="elvie-close" onclick={() => ui.closeElvie()} type="button">×</button>
      </div>
    </header>

    {#if !mode}
      <div class="elvie-mode-pills">
        <!-- Miles 2026-11-05: Analyze (External · Internal) + Life fit. Internal = you name exactly what to check. -->
        {#if workspace.partner_id}<button class="elvie-mode-pill" onclick={() => pickMode('ask')} type="button">Ask</button>{/if}
        <button class="elvie-mode-pill" onclick={() => pickMode('practical')} type="button">Analyze · External</button>
        <button class="elvie-mode-pill" onclick={() => pickMode('internal')} type="button">Analyze · Internal</button>
        <button class="elvie-mode-pill" onclick={() => pickMode('personal')} type="button">Life fit</button>
      </div>
      <div class="elvie-messages">
        <div class="elvie-welcome">
          <p>External: competitors, prices, the market. Internal: name one thing in your company to check. Life fit: does this business match the life you want.</p>
        </div>
      </div>
    {:else if mode === 'segment' && segmentPicker}
      <div class="elvie-segment-picker">
        <h4>Which one do you want to work on?</h4>
        {#if segmentOptions.length === 0}
          <p class="elvie-empty">No segments yet. Build something first.</p>
        {:else}
          <div class="elvie-segment-list">
            {#each segmentOptions as opt}
              <button class="elvie-segment-row" onclick={() => pickSegment(opt)} type="button">
                <span class="elvie-segment-kind">{opt.kind}</span>
                <span class="elvie-segment-name">{opt.name}</span>
              </button>
            {/each}
          </div>
        {/if}
      </div>
    {:else}
      <div class="elvie-messages" bind:this={scrollEl}>
        {#if messages.length === 0}
          <div class="elvie-welcome">
            {#if mode === 'ask'}
              <p>Ask anything about how this company works — offers, roles, steps. The owner can see questions asked here.</p>
            {:else if mode === 'practical'}
              <p>I can look up your competitors, their prices and your market. What do you want to know?</p>
            {:else if mode === 'internal'}
              <p>What exactly should I check? An offer, a step, a page, a role — name it and I'll check it against your buyers, your flow and your team.</p>
            {:else if mode === 'segment'}
              <p>Working on <strong>{pickedSegment?.name}</strong>. What's your question? I'll dig into alignment, process, and what you need.</p>
            {:else if mode === 'personal'}
              <p>I can see your journals, goals, and exercises. Tell me what's on your mind about the business — or what's not adding up between what you're building and the life you want.</p>
            {/if}
          </div>
        {/if}
        {#each messages as msg}
          <div class="elvie-msg" class:user={msg.role === 'user'} class:assistant={msg.role === 'assistant'}>
            <div class="elvie-msg-content">{msg.content}</div>
            {#if msg.role === 'assistant' && msg.used_web_search}
              <div class="elvie-msg-tag">🔎 searched the web</div>
            {/if}
            {#if msg.role === 'assistant' && msg.recommendation}
              <a class="elvie-rec-button" href="https://personal.bestlifeventures.com/exercises/{msg.recommendation.slug}" target="_blank" rel="noopener">
                <span class="elvie-rec-label">Take this exercise →</span>
                <span class="elvie-rec-title">{msg.recommendation.title}</span>
                {#if msg.recommendation.why}<span class="elvie-rec-why">{msg.recommendation.why}</span>{/if}
              </a>
            {/if}
          </div>
        {/each}
        {#if sending}
          <div class="elvie-msg assistant">
            <div class="elvie-msg-content elvie-typing"><span></span><span></span><span></span></div>
          </div>
        {/if}
      </div>

      <div class="elvie-input-row">
        <textarea
          class="elvie-input"
          bind:value={inputText}
          onkeydown={handleKey}
          placeholder={mode === 'practical' ? 'Ask about your industry, competition, anything…' : mode === 'segment' ? `What's the question on ${pickedSegment?.name}?` : "What's on your mind?"}
          rows="2"
          disabled={sending}
        ></textarea>
        <button class="elvie-send" onclick={sendMessage} disabled={sending || !inputText.trim()} type="button" aria-label="Send">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style:opacity={sending ? 0.4 : 1}>
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="14 7 19 12 14 17" />
          </svg>
        </button>
      </div>
    {/if}
  </aside>
{/if}

<style>
  .elvie-for { font-weight: 400; font-size: 0.8em; opacity: 0.7; }
  .elvie-panel {
    position: fixed;
    right: 24px;
    bottom: 24px;
    width: 380px;
    max-width: calc(100vw - 48px);
    max-height: min(620px, calc(100vh - 48px));
    background: linear-gradient(180deg, rgba(20,18,22,0.96), rgba(12,11,14,0.96));
    border: 1px solid var(--border-strong);
    border-radius: 18px;
    box-shadow:
      0 24px 60px rgba(0,0,0,0.6),
      0 0 40px rgba(180,140,255,0.15),
      inset 0 1px 0 rgba(255,255,255,0.06);
    z-index: 51;
    display: flex; flex-direction: column;
    overflow: hidden;
    transform-origin: bottom right;
    animation: elviePopIn 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  }
  @keyframes elviePopIn {
    from { transform: scale(0.6) translate(20px, 20px); opacity: 0; }
    to   { transform: scale(1) translate(0, 0); opacity: 1; }
  }
  .elvie-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 14px 18px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .elvie-header-left { display: flex; gap: 12px; align-items: center; }
  .elvie-header-right { display: flex; gap: 8px; align-items: center; }
  .elvie-avatar-orb {
    width: 28px;
    height: 28px;
    display: block;
  }
  .elvie-header h3 { margin: 0; font-family: 'DM Serif Display', serif; font-size: 18px; color: #f0efe9; }
  .elvie-subtitle { margin: 0; font-size: 11px; color: #9aa0a6; font-family: 'JetBrains Mono', monospace; }
  .elvie-mode-switch {
    background: rgba(255,255,255,0.06); color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    width: 32px; height: 32px; border-radius: 50%;
    cursor: pointer; font-size: 14px; line-height: 1;
  }
  .elvie-mode-switch:hover { background: rgba(255,255,255,0.1); color: #d4af37; }
  .elvie-close {
    background: rgba(255,255,255,0.06); color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    width: 32px; height: 32px; border-radius: 50%;
    cursor: pointer; font-size: 18px; line-height: 1;
  }
  .elvie-close:hover { background: rgba(255,255,255,0.1); color: #f0efe9; }

  .elvie-mode-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 14px 14px 4px;
  }
  .elvie-mode-pill {
    padding: 4px 10px;
    background: transparent;
    border: 1px solid var(--gold-line);
    border-radius: 999px;
    color: var(--gold);
    font-family: var(--font-mono);
    font-size: 9px;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    font-weight: 600;
    cursor: pointer;
  }
  .elvie-mode-pill:hover { background: var(--gold-soft); }

  .elvie-segment-picker {
    flex: 1;
    padding: 20px 18px;
    overflow-y: auto;
  }
  .elvie-segment-picker h4 {
    margin: 0 0 14px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: #9aa0a6;
  }
  .elvie-segment-list { display: flex; flex-direction: column; gap: 6px; }
  .elvie-segment-row {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 10px;
    cursor: pointer;
    text-align: left;
    color: inherit;
    font-family: inherit;
    transition: all 0.12s ease;
  }
  .elvie-segment-row:hover {
    background: rgba(212,175,55,0.06);
    border-color: rgba(212,175,55,0.3);
  }
  .elvie-segment-kind {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px; letter-spacing: 1.2px; text-transform: uppercase;
    color: #9aa0a6;
    min-width: 60px;
  }
  .elvie-segment-name { font-size: 13px; color: #f0efe9; flex: 1; }
  .elvie-empty {
    color: #9aa0a6; font-size: 12px;
    text-align: center;
    padding: 20px;
  }

  .elvie-messages {
    flex: 1; overflow-y: auto;
    padding: 16px 18px;
    display: flex; flex-direction: column; gap: 10px;
  }
  .elvie-welcome {
    padding: 14px;
    background: rgba(212,175,55,0.06);
    border: 1px solid rgba(212,175,55,0.2);
    border-radius: 10px;
    color: #d8d8dc;
    font-size: 13px;
    line-height: 1.5;
  }
  .elvie-welcome p { margin: 0; }
  .elvie-msg {
    display: flex; flex-direction: column;
    max-width: 90%;
  }
  .elvie-msg.user { align-self: flex-end; }
  .elvie-msg.assistant { align-self: flex-start; }
  .elvie-msg-content {
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  .elvie-msg.user .elvie-msg-content {
    background: rgba(212,175,55,0.15);
    border: 1px solid rgba(212,175,55,0.25);
    color: #f0efe9;
  }
  .elvie-msg.assistant .elvie-msg-content {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.06);
    color: #d8d8dc;
  }
  .elvie-msg-tag {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px; letter-spacing: 1px;
    color: #7AB8FF;
    margin-top: 4px;
    padding-left: 6px;
  }
  .elvie-rec-button {
    display: flex; flex-direction: column; gap: 2px;
    margin-top: 10px;
    padding: 10px 14px;
    background: linear-gradient(180deg, rgba(212,175,55,0.12), rgba(212,175,55,0.04));
    border: 1px solid rgba(212,175,55,0.35);
    border-radius: 10px;
    text-decoration: none;
    color: inherit;
    transition: all 0.15s ease;
  }
  .elvie-rec-button:hover {
    background: rgba(212,175,55,0.2);
    border-color: rgba(212,175,55,0.6);
  }
  .elvie-rec-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px; letter-spacing: 1.2px; text-transform: uppercase;
    color: #d4af37;
  }
  .elvie-rec-title {
    font-family: 'DM Serif Display', serif;
    font-size: 15px;
    color: #f0efe9;
  }
  .elvie-rec-why {
    font-size: 11px;
    color: #9aa0a6;
    font-style: italic;
  }
  .elvie-typing {
    display: inline-flex; gap: 4px; align-items: center;
  }
  .elvie-typing span {
    width: 6px; height: 6px; border-radius: 50%;
    background: #9aa0a6;
    animation: typing 1.2s infinite ease-in-out;
  }
  .elvie-typing span:nth-child(2) { animation-delay: 0.15s; }
  .elvie-typing span:nth-child(3) { animation-delay: 0.3s; }
  @keyframes typing {
    0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
    30% { opacity: 1; transform: translateY(-3px); }
  }
  .elvie-input-row {
    display: flex; gap: 8px;
    padding: 12px 14px;
    border-top: 1px solid rgba(255,255,255,0.06);
    background: rgba(255,255,255,0.01);
  }
  .elvie-input {
    flex: 1;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.1);
    color: #f4f4f6;
    padding: 8px 10px;
    border-radius: 8px;
    font-size: 13px;
    font-family: inherit;
    resize: none;
  }
  .elvie-input:focus { outline: none; border-color: rgba(212,175,55,0.4); }
  .elvie-send {
    width: 30px; height: 30px;
    border-radius: 50%;
    border: 1px solid var(--gold-line);
    background: transparent;
    color: var(--gold);
    display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer;
    padding: 0;
    transition: background 0.15s ease, border-color 0.15s ease;
    flex-shrink: 0;
    align-self: flex-end;
  }
  .elvie-send:hover { background: var(--gold-soft); border-color: var(--gold); }
  .elvie-send:disabled { opacity: 0.3; cursor: not-allowed; }
</style>
